/* ==========================================================================
   NeuroSockz — UI behaviours
   Native custom elements only. No dependencies beyond the focus helpers
   already defined globally by Dawn's global.js (trapFocus / removeTrapFocus).
   Loaded with `defer` after global.js, so those helpers are guaranteed present.
   ========================================================================== */
(function () {
  'use strict';

  // Guarded so that loading this file twice (e.g. a section referenced from two
  // templates) can never throw "the name has already been used".
  const defineOnce = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Scroll reveal
     Uses IntersectionObserver and unobserves after firing, so there is no
     ongoing scroll cost. Falls back to showing everything immediately.
     ------------------------------------------------------------------ */
  function initReveal() {
    const targets = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!targets.length) return;

    const enabled = window.neuroSettings?.revealEnabled !== false;

    if (!enabled || prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------
     <neuro-accordion> — FAQ and product tabs
     Buttons + aria-expanded rather than <details>, so the open/close can be
     animated and only one item need be open at a time when requested.
     ------------------------------------------------------------------ */
  class NeuroAccordion extends HTMLElement {
    connectedCallback() {
      this.single = this.hasAttribute('single');
      this.triggers = Array.from(this.querySelectorAll('.accordion__trigger'));
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.toggle(trigger));
      });
    }

    toggle(trigger) {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      if (this.single && !isOpen) {
        this.triggers.forEach((other) => {
          if (other === trigger) return;
          const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
          other.setAttribute('aria-expanded', 'false');
          if (otherPanel) otherPanel.dataset.open = 'false';
        });
      }

      trigger.setAttribute('aria-expanded', String(!isOpen));
      panel.dataset.open = String(!isOpen);
    }
  }
  defineOnce('neuro-accordion', NeuroAccordion);

  /* ------------------------------------------------------------------
     <neuro-drawer> — mobile menu, size guide
     Generic slide-over. Opened by any [data-drawer-open="<id>"] trigger.
     ------------------------------------------------------------------ */
  class NeuroDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer-panel');
      this.overlay = this.querySelector('.drawer-overlay');
      this.opener = null;

      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      });

      document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
      });
    }

    open(trigger) {
      this.opener = trigger || null;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      if (this.opener) this.opener.setAttribute('aria-expanded', 'true');

      const focusTarget = this.querySelector('[data-drawer-close]') || this.panel;
      if (typeof trapFocus === 'function') {
        trapFocus(this.panel, focusTarget);
      } else if (focusTarget) {
        focusTarget.focus();
      }
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      if (this.opener) this.opener.setAttribute('aria-expanded', 'false');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.opener);
      } else if (this.opener) {
        this.opener.focus();
      }
    }
  }
  defineOnce('neuro-drawer', NeuroDrawer);

  /* ------------------------------------------------------------------
     <neuro-header> — sticky behaviour + measured height
     Publishes its own height to --header-h so scroll-padding-top and the
     sticky ATC can position against it without magic numbers.
     ------------------------------------------------------------------ */
  class NeuroHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.setHeight();

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.setHeight());
        this.ro.observe(this);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.ro?.disconnect();
    }

    setHeight() {
      document.documentElement.style.setProperty('--header-h', `${this.offsetHeight}px`);
    }

    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 8);
      this.lastY = y;
    }
  }
  defineOnce('neuro-header', NeuroHeader);

  /* ------------------------------------------------------------------
     <neuro-qty> — quantity stepper
     Dispatches a native `change` event so Dawn's cart listener still fires.
     ------------------------------------------------------------------ */
  class NeuroQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[data-qty-minus]');
      this.plus = this.querySelector('[data-qty-plus]');
      if (!this.input) return;

      this.minus?.addEventListener('click', () => this.step(-1));
      this.plus?.addEventListener('click', () => this.step(1));
      this.input.addEventListener('change', () => this.syncDisabled());
      this.syncDisabled();
    }

    step(direction) {
      const step = parseInt(this.input.step, 10) || 1;
      const current = parseInt(this.input.value, 10) || 0;
      const min = parseInt(this.input.min, 10) || 0;
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const next = Math.min(max, Math.max(min, current + direction * step));

      if (next === current) return;
      this.input.value = String(next);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncDisabled();
    }

    syncDisabled() {
      const current = parseInt(this.input.value, 10) || 0;
      const min = parseInt(this.input.min, 10) || 0;
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      if (this.minus) this.minus.disabled = current <= min;
      if (this.plus) this.plus.disabled = current >= max;
    }
  }
  defineOnce('neuro-qty', NeuroQty);

  /* ------------------------------------------------------------------
     <neuro-gallery> — product media gallery
     CSS scroll-snap does the swiping; JS only syncs the thumbnail state.
     No carousel library, no layout thrash.
     ------------------------------------------------------------------ */
  class NeuroGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      if (!this.viewport || !this.slides.length) return;

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goTo(index));
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.setActive(this.slides.indexOf(entry.target));
              }
            });
          },
          { root: this.viewport, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      this.setActive(0);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.viewport.scrollTo({
        left: slide.offsetLeft - this.viewport.offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
      this.setActive(index);
    }

    setActive(index) {
      if (index < 0) return;
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    /* Called by Dawn's variant change so the gallery jumps to the variant image */
    selectMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.goTo(index);
    }
  }
  defineOnce('neuro-gallery', NeuroGallery);

  /* ------------------------------------------------------------------
     <neuro-sticky-atc> — sticky add-to-cart bar
     Appears once the real add-to-cart button has scrolled out of view.
     Submits the real form rather than duplicating cart logic.
     ------------------------------------------------------------------ */
  class NeuroStickyAtc extends HTMLElement {
    connectedCallback() {
      const anchorId = this.dataset.anchor;
      const anchor = anchorId ? document.getElementById(anchorId) : null;
      if (!anchor || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          // Show only when the real ATC has scrolled ABOVE the viewport,
          // never when it is still below the fold on first paint.
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', scrolledPast);
        },
        { threshold: 0 }
      );
      this.observer.observe(anchor);

      this.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
        const form = document.getElementById(this.dataset.form);
        form?.querySelector('[type="submit"]')?.click();
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  defineOnce('neuro-sticky-atc', NeuroStickyAtc);

  /* ------------------------------------------------------------------
     <neuro-tabs> — product information tabs
     Full roving-tabindex keyboard support per the WAI-ARIA tabs pattern.
     ------------------------------------------------------------------ */
  class NeuroTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.select(0);
    }

    onKeydown(event, index) {
      const keys = { ArrowRight: 1, ArrowLeft: -1 };
      if (event.key in keys) {
        event.preventDefault();
        const next = (index + keys[event.key] + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.select(0);
        this.tabs[0].focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        const last = this.tabs.length - 1;
        this.select(last);
        this.tabs[last].focus();
      }
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      this.panels.forEach((panel, i) => {
        panel.toggleAttribute('hidden', i !== index);
      });
    }
  }
  defineOnce('neuro-tabs', NeuroTabs);

  /* ------------------------------------------------------------------
     <neuro-cart-upsell> — cart drawer recommendations
     Fetches server-rendered markup from the Product Recommendations API so
     all pricing and availability logic stays in Liquid.
     ------------------------------------------------------------------ */
  class NeuroCartUpsell extends HTMLElement {
    connectedCallback() {
      const { url, productId, sectionId, limit } = this.dataset;
      if (!url || !productId || this.dataset.loaded === 'true') return;

      const endpoint =
        `${url}?section_id=${encodeURIComponent(sectionId || 'cart-recommendations')}` +
        `&product_id=${encodeURIComponent(productId)}` +
        `&limit=${encodeURIComponent(limit || 3)}` +
        `&intent=complementary`;

      fetch(endpoint)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const inner = html.querySelector('.cart-upsell__inner');
          // No complementary products configured is a normal outcome, not an error.
          if (!inner) return;
          this.innerHTML = inner.outerHTML;
          this.dataset.loaded = 'true';
        })
        .catch(() => {
          // Recommendations are non-essential; fail silently rather than
          // showing a broken block inside the cart.
          this.remove();
        });
    }
  }
  defineOnce('neuro-cart-upsell', NeuroCartUpsell);

  /* ------------------------------------------------------------------
     <neuro-countdown> — promotional countdown

     Counts down to a real end date. When that date passes the whole section
     removes itself rather than resetting, so this can never become an
     evergreen fake-urgency timer.
     ------------------------------------------------------------------ */
  class NeuroCountdown extends HTMLElement {
    connectedCallback() {
      const end = Date.parse(this.dataset.end || '');
      if (Number.isNaN(end)) {
        this.remove();
        return;
      }

      this.end = end;
      this.fields = {
        days: this.querySelector('[data-cd-days]'),
        hours: this.querySelector('[data-cd-hours]'),
        mins: this.querySelector('[data-cd-mins]'),
        secs: this.querySelector('[data-cd-secs]'),
      };

      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const remaining = this.end - Date.now();

      if (remaining <= 0) {
        window.clearInterval(this.timer);
        // The offer is over — take the whole band down, not just the timer.
        this.closest('.promo-band-wrap')?.remove();
        return;
      }

      const secs = Math.floor(remaining / 1000);
      const pad = (n) => String(n).padStart(2, '0');

      if (this.fields.days) this.fields.days.textContent = pad(Math.floor(secs / 86400));
      if (this.fields.hours) this.fields.hours.textContent = pad(Math.floor((secs % 86400) / 3600));
      if (this.fields.mins) this.fields.mins.textContent = pad(Math.floor((secs % 3600) / 60));
      if (this.fields.secs) this.fields.secs.textContent = pad(secs % 60);
    }
  }
  defineOnce('neuro-countdown', NeuroCountdown);

  /* ------------------------------------------------------------------
     Delegated handlers

     These replace inline onclick/onchange attributes, which a strict
     Content-Security-Policy blocks. Delegation also survives the cart drawer
     being re-rendered by AJAX, which per-element listeners would not.
     ------------------------------------------------------------------ */
  function initDelegates() {
    document.addEventListener('click', (event) => {
      const closer = event.target.closest('[data-cart-drawer-close]');
      if (closer) {
        event.preventDefault();
        closer.closest('cart-drawer')?.close?.();
      }
    });

    // Localization selectors submit their form on change.
    document.addEventListener('change', (event) => {
      const el = event.target;
      if (el instanceof HTMLElement && el.matches('[data-auto-submit]')) {
        el.closest('form')?.submit();
      }
    });

    /* Header dropdowns are native <details>, which stay open until their own
       summary is clicked again. Close them on outside click and on Escape so
       they behave like the menus people expect. */
    const closeMenus = (except) => {
      document.querySelectorAll('[data-header-menu][open]').forEach((menu) => {
        if (menu !== except) menu.removeAttribute('open');
      });
    };

    document.addEventListener('click', (event) => {
      const inside = event.target.closest?.('[data-header-menu]');
      closeMenus(inside);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = document.querySelector('[data-header-menu][open]');
      if (!open) return;
      closeMenus(null);
      open.querySelector('summary')?.focus();
    });

    // Moving focus out of a menu with the keyboard should close it too.
    document.addEventListener('focusin', (event) => {
      closeMenus(event.target.closest?.('[data-header-menu]'));
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function boot() {
    initReveal();
    initDelegates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Sections re-rendered by the theme editor or cart AJAX need re-scanning
  document.addEventListener('shopify:section:load', initReveal);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, initReveal);
  }
})();
